import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Upload, Trash2, LogOut, Music, ArrowLeft, RefreshCw, ArrowUp, ArrowDown, Disc3 } from "lucide-react";
import api, { formatErr, formatTime } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VoiceDropInbox from "@/components/VoiceDropInbox";
import KingRequestInbox from "@/components/KingRequestInbox";

const GENRES = ["House", "EDM", "Cumbia", "Salsa", "Bachata", "Reggaeton", "Latin", "Tech House"];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mixes, setMixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", artist: "", genre: "House", file: null });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchMixes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mixes");
      setMixes(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMixes(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    if (!form.file) { setError("Select an audio file."); return; }
    setUploading(true);
    setUploadProgress(0);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("artist", form.artist);
    fd.append("genre", form.genre);
    fd.append("file", form.file);
    try {
      const { data } = await api.post("/mixes", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploadProgress(pct);
          } else if (evt.loaded) {
            // total unknown — show indeterminate cap at 95% until response
            setUploadProgress((prev) => Math.min(95, prev + 1));
          }
        },
      });
      setUploadProgress(100);
      setSuccess(`Added "${data.title}" (${formatTime(data.duration)})`);
      setForm({ title: "", artist: "", genre: "House", file: null });
      document.getElementById("mix-file-input").value = "";
      fetchMixes();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
      // brief delay so user sees 100% before bar resets
      setTimeout(() => setUploadProgress(0), 800);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mix from the broadcast?")) return;
    try {
      await api.delete(`/mixes/${id}`);
      fetchMixes();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const handleMove = async (idx, dir) => {
    const newOrder = [...mixes];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    setMixes(newOrder);
    try {
      await api.post("/mixes/reorder", { ids: newOrder.map((m) => m.id) });
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
      fetchMixes();
    }
  };

  const handleResetClock = async () => {
    if (!window.confirm("Reset broadcast clock? The playlist will restart from track 1 right now.")) return;
    try {
      await api.post("/station/reset-clock");
      setSuccess("Broadcast clock reset. Playlist restarted.");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const totalDuration = mixes.reduce((acc, m) => acc + (m.duration || 0), 0);

  return (
    <div className="min-h-screen px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00f2fe] to-[#7a1fa2] flex items-center justify-center font-display font-black text-lg">
            MF
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--neon-cyan)]">
              DJ Console
            </p>
            <h1 className="font-display text-2xl font-bold leading-tight">Broadcast Control</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/" className="btn-ghost flex items-center gap-1.5" data-testid="back-to-player">
            <ArrowLeft size={14} /> Player
          </Link>
          <button onClick={handleResetClock} className="btn-ghost flex items-center gap-1.5" data-testid="reset-clock-button">
            <RefreshCw size={14} /> Reset Clock
          </button>
          <button onClick={handleLogout} className="btn-ghost flex items-center gap-1.5" data-testid="logout-button">
            <LogOut size={14} /> {user?.name || "Logout"}
          </button>
        </div>
      </header>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Mixes" value={mixes.length} suffix="tracks" testId="stat-mixes" />
        <StatCard label="Total Airtime" value={formatTime(totalDuration)} testId="stat-airtime" />
        <StatCard label="Status" value="LIVE" accent testId="stat-status" />
        <StatCard label="DJ" value="JAVI G" testId="stat-dj" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Upload form */}
        <section className="admin-card p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-1">
            <Upload size={16} className="text-[var(--neon-cyan)]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--neon-cyan)]">
              Add a Mix
            </span>
          </div>
          <h2 className="font-display text-xl font-bold mb-5">Upload to Playlist</h2>

          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
                Title
              </label>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="input-dark"
                placeholder="Saturday Night Set Vol. 3"
                data-testid="upload-title-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
                Artist
              </label>
              <input
                required
                value={form.artist}
                onChange={(e) => setForm({ ...form, artist: e.target.value })}
                className="input-dark"
                placeholder="DJ JAVI G"
                data-testid="upload-artist-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
                Genre
              </label>
              <select
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                className="input-dark"
                data-testid="upload-genre-select"
              >
                {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] block mb-1.5">
                Audio File (MP3, M4A, WAV, OGG · max 200MB)
              </label>
              <input
                id="mix-file-input"
                type="file"
                accept="audio/*,.mp3,.m4a,.wav,.ogg"
                required
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="input-dark file:bg-[var(--neon-cyan)] file:text-black file:font-bold file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:cursor-pointer"
                data-testid="upload-file-input"
              />
            </div>

            {error && <p className="text-[#ff5566] text-sm font-mono" data-testid="upload-error">{error}</p>}
            {success && <p className="text-[var(--neon-cyan)] text-sm font-mono" data-testid="upload-success">{success}</p>}

            <button
              type="submit"
              disabled={uploading}
              className="btn-cyan w-full relative overflow-hidden flex items-center justify-center gap-2 py-3"
              data-testid="upload-submit-button"
            >
              {uploading && (
                <span
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#7a1fa2]/40 via-[#00f2fe]/40 to-[#7a1fa2]/40 transition-[width] duration-150 ease-out pointer-events-none"
                  style={{ width: `${uploadProgress}%` }}
                  data-testid="upload-progress-fill"
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                <Upload size={16} />
                {uploading ? (
                  <span data-testid="upload-progress-label">
                    Uploading… <span className="font-mono">{uploadProgress}%</span>
                  </span>
                ) : (
                  "Add to Broadcast"
                )}
              </span>
            </button>
          </form>
        </section>

        {/* Playlist */}
        <section className="admin-card p-6 lg:col-span-3">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Music size={16} className="text-[var(--brand-purple-light)]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--brand-purple-light)]">
                  Broadcast Queue
                </span>
              </div>
              <h2 className="font-display text-xl font-bold">Playlist Rotation</h2>
            </div>
            <button onClick={fetchMixes} className="btn-ghost flex items-center gap-1.5" data-testid="refresh-mixes-button">
              <RefreshCw size={14} />
            </button>
          </div>

          {loading ? (
            <p className="text-[var(--text-muted)] text-sm font-mono py-8 text-center">Loading playlist...</p>
          ) : mixes.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-[var(--border-soft)] rounded-lg" data-testid="empty-playlist">
              <Disc3 size={36} className="mx-auto text-[var(--text-muted)] mb-3 opacity-50" />
              <p className="text-[var(--text-muted)] text-sm">No mixes uploaded yet.</p>
              <p className="text-[var(--text-muted)] text-xs font-mono mt-1">Add your first track to start broadcasting.</p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="mix-list">
              {mixes.map((mix, idx) => (
                <li
                  key={mix.id}
                  data-testid={`mix-row-${mix.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#0d0d16] border border-[var(--border-soft)] hover:border-[var(--border-cyan)] transition-colors"
                >
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMove(idx, -1)}
                      disabled={idx === 0}
                      className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)] disabled:opacity-20 disabled:hover:text-[var(--text-muted)]"
                      data-testid={`move-up-${mix.id}`}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      onClick={() => handleMove(idx, 1)}
                      disabled={idx === mixes.length - 1}
                      className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)] disabled:opacity-20 disabled:hover:text-[var(--text-muted)]"
                      data-testid={`move-down-${mix.id}`}
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>

                  <div className="font-mono text-xs text-[var(--text-muted)] w-6 text-right">
                    {String(idx + 1).padStart(2, "0")}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{mix.title}</p>
                    <p className="text-xs text-[var(--text-muted)] font-mono truncate">
                      {mix.artist} · {mix.genre}
                      {mix.storage === "cloudinary" && (
                        <span className="ml-2 text-[var(--neon-cyan)]" title="Stored on Cloudinary CDN">
                          · ☁ CDN
                        </span>
                      )}
                    </p>
                  </div>

                  <div className="font-mono text-xs text-[var(--neon-cyan)]">
                    {formatTime(mix.duration)}
                  </div>

                  <button
                    onClick={() => handleDelete(mix.id)}
                    className="btn-danger flex items-center gap-1"
                    data-testid={`delete-mix-${mix.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Voice Drops Inbox */}
      <VoiceDropInbox />

      {/* Royal Requests Inbox (secret unlock) */}
      <KingRequestInbox />

      <footer className="mt-12 flex items-center justify-center gap-2 pt-6 border-t border-[var(--border-soft)]">
        <span className="brand-powered text-xs">powered by</span>
        <span className="brand-javig text-lg">JAVI G</span>
      </footer>
    </div>
  );
}

function StatCard({ label, value, suffix, accent, testId }) {
  return (
    <div className="admin-card p-4" data-testid={testId}>
      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5">
        {label}
      </p>
      <p className={`font-display text-2xl font-bold ${accent ? "text-[var(--neon-cyan)]" : "text-white"}`}>
        {value} {suffix && <span className="text-xs font-mono text-[var(--text-muted)]">{suffix}</span>}
      </p>
    </div>
  );
}
